import { useState, useEffect } from 'react';

import { Security } from '@okta/okta-react';
import { getIsCypressOnLocalhost } from '@sureapp/platform-sdk';
import axios from 'axios';
import localforage from 'localforage';
import { Helmet } from 'react-helmet';
import { Provider } from 'react-redux';
import { StripeProvider } from 'react-stripe-elements';
import { PersistGate } from 'redux-persist/integration/react';

import api from '@@/api';
import SpinnerView from '@@/auto/components/SpinnerView';
import configWithDefaults from '@@/auto/constants/wlConfig';
import { ConfigProvider } from '@@/auto/services/useConfig';
import { store, persistor } from '@@/auto/store';
import { getCmsContent, getFeatureFlags } from '@@/auto/store/actions';
import { identifyUserToAllServices } from '@@/auto/utils/analytics';
import TenantTheme from '@@/common/themes/TenantTheme';

import Router from './Router';

/*
 * Axios config
 */

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error(error);
    return Promise.reject(error);
  }
);

const SureAuth = () => {
  const { oktaAccessToken, oktaIdToken, user } = store.getState();
  // Set global Auth header
  if (oktaAccessToken) {
    api.setAuthHeader(oktaAccessToken.accessToken);
  }

  if (oktaIdToken) {
    identifyUserToAllServices(oktaIdToken.claims, user || {});
  }
  return null;
};

export default function App() {
  const [isLoadingFeatureFlags, setIsLoadingFeatureFlags] = useState(false);
  const [stripe, setStripe] = useState<ReturnType<typeof window.Stripe> | null>(
    null
  );

  const { localforageDbName, cssFiles } = configWithDefaults;

  /**
   * Configure localforage configs
   */
  useEffect(
    function configureLocalforage() {
      localforage.config({
        name: localforageDbName,
        storeName: localforageDbName,
      });
    },
    [localforageDbName]
  );

  useEffect(function loadCmsContent() {
    getCmsContent(['footer', 'copyright'])(store.dispatch);
  }, []);

  useEffect(function loadFeatureFlags() {
    async function fetchFlags() {
      setIsLoadingFeatureFlags(true);
      try {
        await getFeatureFlags()(store.dispatch);
      } catch {
        /* fallback to feature flag defaults */
      }
      setIsLoadingFeatureFlags(false);
    }

    fetchFlags();
  }, []);

  useEffect(
    function setUpStripe() {
      const stripeScript = document.querySelector('#stripe-js');
      if (window.Stripe) {
        setStripe(
          window.Stripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '')
        );
      } else if (stripeScript) {
        stripeScript.addEventListener('load', () => {
          setStripe(
            window.Stripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '')
          );
        });
      }
    },
    [setStripe]
  );

  if (isLoadingFeatureFlags) {
    return <SpinnerView data-testid="App_spinner" />;
  }

  if (window.Cypress) {
    /*
     * Loads the store into Cypress which allows us to
     * manipulate and set up the store through integration tests
     */
    window.store = store;
  }

  function getVWOScript() {
    if (process.env.REACT_APP_TENANT === 'carvana') {
      return (
        <Helmet
          script={[
            {
              type: 'text/javascript',
              innerHTML: `
              window._vwo_code = window._vwo_code || (function(){
              var account_id=503070,
              settings_tolerance=2000,
              library_tolerance=2500,
              use_existing_jquery=false,
              is_spa=1,
              hide_element='body',

              /* DO NOT EDIT BELOW THIS LINE */
              f=false,d=document,code={use_existing_jquery:function(){return use_existing_jquery;},library_tolerance:function(){return library_tolerance;},finish:function(){if(!f){f=true;var a=d.getElementById('_vis_opt_path_hides');if(a)a.parentNode.removeChild(a);}},finished:function(){return f;},load:function(a){var b=d.createElement('script');b.src=a;b.type='text/javascript';b.innerText;b.onerror=function(){_vwo_code.finish();};d.getElementsByTagName('head')[0].appendChild(b);},init:function(){
              window.settings_timer=setTimeout(function () {_vwo_code.finish() },settings_tolerance);var a=d.createElement('style'),b=hide_element?hide_element+'{opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important;}':'',h=d.getElementsByTagName('head')[0];a.setAttribute('id','_vis_opt_path_hides');a.setAttribute('type','text/css');if(a.styleSheet)a.styleSheet.cssText=b;else a.appendChild(d.createTextNode(b));h.appendChild(a);this.load('https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&f='+(+is_spa)+'&r='+Math.random());return settings_timer; }};window._vwo_settings_timer = code.init(); return code; }());
            `,
            },
          ]}
        />
      );
    }
    if (process.env.REACT_APP_TENANT === 'credit_karma') {
      return (
        <Helmet
          script={[
            {
              type: 'text/javascript',
              innerHTML: `
                window._vwo_code = window._vwo_code || (function(){
                var account_id=548382,
                settings_tolerance=2000,
                library_tolerance=2500,
                use_existing_jquery=false,
                is_spa=1,
                hide_element='body',

                /* DO NOT EDIT BELOW THIS LINE */
                f=false,d=document,code={use_existing_jquery:function(){return use_existing_jquery;},library_tolerance:function(){return library_tolerance;},finish:function(){if(!f){f=true;var a=d.getElementById('_vis_opt_path_hides');if(a)a.parentNode.removeChild(a);}},finished:function(){return f;},load:function(a){var b=d.createElement('script');b.src=a;b.type='text/javascript';b.innerText;b.onerror=function(){_vwo_code.finish();};d.getElementsByTagName('head')[0].appendChild(b);},init:function(){
                window.settings_timer=setTimeout(function () {_vwo_code.finish() },settings_tolerance);var a=d.createElement('style'),b=hide_element?hide_element+'{opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important;}':'',h=d.getElementsByTagName('head')[0];a.setAttribute('id','_vis_opt_path_hides');a.setAttribute('type','text/css');if(a.styleSheet)a.styleSheet.cssText=b;else a.appendChild(d.createTextNode(b));h.appendChild(a);this.load('https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&f='+(+is_spa)+'&r='+Math.random());return settings_timer; }};window._vwo_settings_timer = code.init(); return code; }());
              `,
            },
          ]}
        />
      );
    }
    return (
      <Helmet
        script={[
          {
            type: 'text/javascript',
            innerHTML: `
              var _vwo_code=(function(){
              var account_id=377647,
              settings_tolerance=2000,
              library_tolerance=2500,
              use_existing_jquery=false,
              is_spa = 1,

              /* DO NOT EDIT BELOW THIS LINE */
              f=false,d=document;return{use_existing_jquery:function(){return use_existing_jquery;},library_tolerance:function(){return library_tolerance;},finish:function(){if(!f){f=true;var a=d.getElementById('_vis_opt_path_hides');if(a)a.parentNode.removeChild(a);}},finished:function(){return f;},load:function(a){var b=d.createElement('script');b.src=a;b.type='text/javascript';b.innerText;b.onerror=function(){_vwo_code.finish();};d.getElementsByTagName('head')[0].appendChild(b);},init:function(){settings_timer=setTimeout('_vwo_code.finish()',settings_tolerance);var a=d.createElement('style'),b='body{opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important;}',h=d.getElementsByTagName('head')[0];a.setAttribute('id','_vis_opt_path_hides');a.setAttribute('type','text/css');if(a.styleSheet)a.styleSheet.cssText=b;else a.appendChild(d.createTextNode(b));h.appendChild(a);this.load('//dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&f='+(+is_spa)+'&r='+Math.random());return settings_timer;}};}());_vwo_settings_timer=_vwo_code.init();
            `,
          },
        ]}
      />
    );
  }

  return (
    <TenantTheme cssFiles={cssFiles}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <ConfigProvider>
            <StripeProvider stripe={stripe}>
              <Security
                issuer={process.env.REACT_APP_OKTA_ISSUER}
                clientId={process.env.REACT_APP_OKTA_CLIENT_ID}
                redirectUri={`${window.location.origin}${process.env.REACT_APP_OKTA_REDIRECT_URI}`}
                responseType={['id_token', 'token']}
                onSessionExpired={() => {}}
                pkce={false}
                cookies={{
                  secure: process.env.REACT_APP_ENV !== 'development',
                }}
              >
                {!getIsCypressOnLocalhost() && getVWOScript()}
                <SureAuth />
                <Router />
              </Security>
            </StripeProvider>
          </ConfigProvider>
        </PersistGate>
      </Provider>
    </TenantTheme>
  );
}
